/**
 * The lesson-page design stage.
 *
 * Every block stores raw pixel left/top/width/height. Those numbers are meaningless without a
 * fixed frame to be relative to, which is what this is: blocks are laid out in a
 * STAGE_WIDTH x STAGE_HEIGHT box that is then uniformly transform: scale()d to fit whatever the
 * viewport happens to be. Scaling rather than re-flowing is what keeps a page identical --
 * proportions, overlaps, font sizes and all -- on a phone, a laptop, in a template preview and
 * while presenting.
 *
 * Before this existed the canvas WAS the viewport (.blocks-container is `width: calc(100% - 20px)`),
 * so the same block sat at a different fraction of the page on every screen size, and a slide laid
 * out on a desktop overflowed a phone entirely.
 *
 * 16:9, matching the aspect every slideshow surface is authored for. The width is deliberately
 * close to the old fluid canvas width on a desktop editor so existing block coordinates keep
 * roughly the position they were authored at.
 */
export const STAGE_WIDTH = 1400;
export const STAGE_HEIGHT = 788; // ~16:9

/**
 * Uniform "contain" scale: the largest factor that still fits the whole stage inside the viewport
 * on BOTH axes. Contain rather than width-fit because a page that is cropped at the bottom is a
 * page with missing content -- the exact slideshow-on-mobile failure this replaces.
 */
export const getStageScale = (viewportWidth, viewportHeight) => {
    if (!viewportWidth || !viewportHeight) return 1;
    const scale = Math.min(viewportWidth / STAGE_WIDTH, viewportHeight / STAGE_HEIGHT);
    return scale > 0 ? scale : 1;
};

/**
 * The live scale of a rendered .blocks-stage, read back off the DOM.
 *
 * getBoundingClientRect() reports POST-transform (scaled) pixels while offsetWidth is the
 * pre-transform layout width, so their ratio is the scale currently in effect -- no need to thread
 * React state into every pointer handler, and it stays correct mid-animation.
 */
export const readStageScale = (stageEl) => {
    if (!stageEl?.offsetWidth) return 1;
    const scale = stageEl.getBoundingClientRect().width / stageEl.offsetWidth;
    return scale > 0 ? scale : 1;
};

/** The .blocks-stage a pointer event landed in, from any descendant (or the container itself). */
export const findStageEl = (el) => {
    if (!el?.closest) return null;
    return el.closest('.blocks-stage') || el.closest('.blocks-container')?.querySelector('.blocks-stage') || null;
};

/**
 * Convert a client (viewport) point to stage coordinates -- the space block.left/top live in.
 * Every handler that turns a cursor position into a stored coordinate has to go through this,
 * otherwise it writes scaled pixels into a field read back as stage pixels.
 */
export const clientPointToStage = (stageEl, clientX, clientY) => {
    if (!stageEl) return { x: 0, y: 0, scale: 1 };
    const rect = stageEl.getBoundingClientRect();
    const scale = readStageScale(stageEl);
    return {
        x: (clientX - rect.left) / scale,
        y: (clientY - rect.top) / scale,
        scale,
    };
};
